/**
 * Excel工具函数
 * 用于处理Excel文件的导入导出操作
 */

import * as XLSX from 'xlsx';
import XLSX_STYLE from 'xlsx-js-style';

/**
 * 导出失败记录到Excel文件
 * @param {Array} failedRecords - 失败记录数组
 * @param {string} filename - 文件名（可选）
 */
export const exportFailedRecordsToExcel = (failedRecords, filename) => {
    try {
        if (!failedRecords || failedRecords.length === 0) {
            throw new Error('没有失败记录可导出');
        }

        // 准备数据：保留所有原始列 + 错误原因
        const data = failedRecords.map(record => ({
            '航班日期': record.flight_date || '',
            '航班号': record.flight_number || '',
            '始发站': record.departure_airport || '',
            '目的站': record.arrival_airport || '',
            '时区': record.timezone || '',
            '费用类型': record.cost_type || '',
            '供应商': record.supplier_code || '',
            '币种': record.currency || '',
            '金额': record.amount || '',
            '汇率': record.exchange_rate || '',
            '错误原因': record.errorMessage || '未知错误'
        }));
        
        // 创建工作簿
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '失败记录');
        
        // 设置列宽
        const colWidths = [
            { wch: 12 }, // 航班日期
            { wch: 10 }, // 航班号
            { wch: 8 },  // 始发站
            { wch: 8 },  // 目的站
            { wch: 8 },  // 时区
            { wch: 15 }, // 费用类型
            { wch: 20 }, // 供应商
            { wch: 8 },  // 币种
            { wch: 12 }, // 金额
            { wch: 10 }, // 汇率
            { wch: 40 }  // 错误原因
        ];
        ws['!cols'] = colWidths;
        
        // 下载文件
        const defaultFilename = `导入失败记录_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, filename || defaultFilename);
        
        return true;
    } catch (error) {
        console.error('导出失败记录时出错:', error);
        throw error;
    }
};

/**
 * 导出数据到Excel文件
 * @param {Array} data - 数据数组
 * @param {string} sheetName - 工作表名称
 * @param {string} filename - 文件名
 */
export const exportDataToExcel = (data, sheetName = 'Sheet1', filename = 'export.xlsx') => {
    try {
        if (!data || data.length === 0) {
            throw new Error('没有数据可导出');
        }

        // 创建工作簿
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        
        // 下载文件
        XLSX.writeFile(wb, filename);
        
        return true;
    } catch (error) {
        console.error('导出Excel时出错:', error);
        throw error;
    }
};

/**
 * 读取Excel文件
 * @param {File} file - Excel文件对象
 * @returns {Promise<Array>} 解析后的数据数组
 */
export const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                
                // 获取第一个工作表
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // 转换为JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                resolve(jsonData);
            } catch (error) {
                reject(new Error('Excel文件解析失败: ' + error.message));
            }
        };
        
        reader.onerror = () => {
            reject(new Error('文件读取失败'));
        };
        
        reader.readAsArrayBuffer(file);
    });
};

/**
 * 创建Excel模板
 * @param {Array} headers - 表头数组
 * @param {Array} sampleData - 示例数据数组（可选）
 * @param {string} filename - 文件名
 */
export const createExcelTemplate = (headers, sampleData = [], filename = 'template.xlsx') => {
    try {
        // 准备数据
        const data = [headers];
        if (sampleData.length > 0) {
            data.push(...sampleData);
        }
        
        // 创建工作簿
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '模板');
        
        // 下载文件
        XLSX.writeFile(wb, filename);
        
        return true;
    } catch (error) {
        console.error('创建Excel模板时出错:', error);
        throw error;
    }
};

// ─── 成本报表专用导出（带样式 + 航段子行）───

const COST_FIELDS = [
    { key: 'acmi', label: 'ACMI' },
    { key: 'fuel', label: 'Fuel' },
    { key: 'navigation', label: 'Navigation' },
    { key: 'ground_handling', label: 'Ground Handling' },
    { key: 'landing_parking', label: 'Landing/Parking' },
    { key: 'catering', label: 'Catering' },
    { key: 'transportation', label: 'Transportation' },
    { key: 'trucking_fee', label: 'Trucking Fee' },
    { key: 'cargo_handling', label: 'Cargo Handling' },
    { key: 'traffic_rights_permit', label: 'Traffic Rights Permit' },
    { key: 'traffic_rights', label: 'Traffic Rights' },
    { key: 'positioning', label: 'Positioning' },
    { key: 'uld', label: 'ULD' },
    { key: 'sita', label: 'SITA' },
    { key: 'deicing', label: 'Deicing' },
    { key: 'accommodation', label: 'Accommodation' },
    { key: 'laundry', label: 'Laundry' },
    { key: 'disinfection', label: 'Disinfection' },
    { key: 'flight_service_charge', label: 'Flight Svc Charge' },
    { key: 'others_fee', label: 'Others' },
    { key: 'commission', label: 'Commission' },
];

const STYLES = {
    header: {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: '1F4E79' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
            top: { style: 'thin', color: { rgb: '8DB4E2' } },
            bottom: { style: 'thin', color: { rgb: '8DB4E2' } },
            left: { style: 'thin', color: { rgb: '8DB4E2' } },
            right: { style: 'thin', color: { rgb: '8DB4E2' } },
        },
    },
    routeRow: {
        font: { bold: true, sz: 11 },
        fill: { fgColor: { rgb: 'D6E4F0' } },
        alignment: { vertical: 'center' },
        border: {
            top: { style: 'thin', color: { rgb: 'B4C6E7' } },
            bottom: { style: 'thin', color: { rgb: 'B4C6E7' } },
            left: { style: 'thin', color: { rgb: 'B4C6E7' } },
            right: { style: 'thin', color: { rgb: 'B4C6E7' } },
        },
    },
    legRow: {
        font: { sz: 10, color: { rgb: '404040' } },
        fill: { fgColor: { rgb: 'F5F5F5' } },
        alignment: { vertical: 'center' },
        border: {
            top: { style: 'hair', color: { rgb: 'D9D9D9' } },
            bottom: { style: 'hair', color: { rgb: 'D9D9D9' } },
            left: { style: 'hair', color: { rgb: 'D9D9D9' } },
            right: { style: 'hair', color: { rgb: 'D9D9D9' } },
        },
    },
    totalRow: {
        font: { bold: true, sz: 11 },
        fill: { fgColor: { rgb: 'FFF2CC' } },
        alignment: { vertical: 'center' },
        border: {
            top: { style: 'medium', color: { rgb: 'D6B656' } },
            bottom: { style: 'medium', color: { rgb: 'D6B656' } },
            left: { style: 'thin', color: { rgb: 'D6B656' } },
            right: { style: 'thin', color: { rgb: 'D6B656' } },
        },
    },
};

const costNumStyle = (base) => ({
    ...base,
    alignment: { ...base.alignment, horizontal: 'right' },
    numFmt: '#,##0.00',
});

const hoursNumStyle = (base) => ({
    ...base,
    alignment: { ...base.alignment, horizontal: 'right' },
    numFmt: '0.00',
});

function buildCell(value, style) {
    if (value === null || value === undefined || value === '') {
        return { v: '', t: 's', s: style };
    }
    if (typeof value === 'number') {
        return { v: value, t: 'n', s: style };
    }
    return { v: String(value), t: 's', s: style };
}

/**
 * 导出成本报表到Excel（带样式 + 航段子行）
 * @param {Array} data - 航线实例数据（含 legs、cost_summary）
 * @param {Object} options - { filename, formatTime }
 */
export const exportProfitReportToExcel = (data, options = {}) => {
    const {
        filename = '成本报表.xlsx',
        formatTime = (t) => t || '',
    } = options;

    if (!data || data.length === 0) {
        throw new Error('没有数据可导出');
    }

    const headers = [
        '类型', '航线/航班', '执行/起飞时间', '飞机号', '航段数/段次',
        'BH (h)', 'FH (h)',
        ...COST_FIELDS.map(f => f.label),
        'Total (USD)',
    ];
    const colCount = headers.length;

    const rows = [];

    // 表头
    rows.push(headers.map(h => buildCell(h, STYLES.header)));

    let grandTotal = 0;

    data.forEach(r => {
        const cs = r.cost_summary || {};
        const aircraftRegs = [...new Set((r.legs || []).map(l => l.aircraft_reg).filter(Boolean))].join(', ');
        const routeTotal = Number(r.total_cost) || 0;
        grandTotal += routeTotal;

        // 航线实例行（父行）
        const routeStyle = STYLES.routeRow;
        const routeRow = [
            buildCell('航线', routeStyle),
            buildCell(r.route_code || '', routeStyle),
            buildCell(formatTime(r.first_flight_time), routeStyle),
            buildCell(aircraftRegs, routeStyle),
            buildCell(r.leg_count || 0, { ...routeStyle, alignment: { ...routeStyle.alignment, horizontal: 'center' } }),
            buildCell(cs.bh ? Number(cs.bh) : '', hoursNumStyle(routeStyle)),
            buildCell(cs.fh ? Number(cs.fh) : '', hoursNumStyle(routeStyle)),
            ...COST_FIELDS.map(f => buildCell(cs[f.key] ? Number(cs[f.key]) : '', costNumStyle(routeStyle))),
            buildCell(routeTotal, costNumStyle({ ...routeStyle, font: { ...routeStyle.font, color: { rgb: 'C65911' } } })),
        ];
        rows.push(routeRow);

        // 航段子行
        (r.legs || []).forEach(leg => {
            const legStyle = STYLES.legRow;
            const legTotal = Number(leg.total_cost) || 0;
            const legRow = [
                buildCell('  航段', legStyle),
                buildCell(`${leg.flight_number || ''} ${leg.leg || ''}`, legStyle),
                buildCell(formatTime(leg.atd), legStyle),
                buildCell(leg.aircraft_reg || '', legStyle),
                buildCell(leg.leg_sequence || '', { ...legStyle, alignment: { ...legStyle.alignment, horizontal: 'center' } }),
                buildCell(leg.bh ? Number(leg.bh) : '', hoursNumStyle(legStyle)),
                buildCell(leg.fh ? Number(leg.fh) : '', hoursNumStyle(legStyle)),
                ...COST_FIELDS.map(f => buildCell(leg[f.key] ? Number(leg[f.key]) : '', costNumStyle(legStyle))),
                buildCell(legTotal, costNumStyle(legStyle)),
            ];
            rows.push(legRow);
        });
    });

    // 合计行
    const totalStyle = STYLES.totalRow;
    const totalRow = Array(colCount).fill(null).map(() => buildCell('', totalStyle));
    totalRow[0] = buildCell('合计', totalStyle);
    totalRow[1] = buildCell(`${data.length} 条航线`, totalStyle);
    totalRow[colCount - 1] = buildCell(grandTotal, costNumStyle({ ...totalStyle, font: { ...totalStyle.font, sz: 12, color: { rgb: 'C00000' } } }));
    rows.push(totalRow);

    // 构建 worksheet
    const ws = XLSX_STYLE.utils.aoa_to_sheet(rows);

    // 列宽
    ws['!cols'] = [
        { wch: 7 },   // 类型
        { wch: 22 },  // 航线/航班
        { wch: 18 },  // 时间
        { wch: 14 },  // 飞机号
        { wch: 10 },  // 航段数
        { wch: 9 },   // BH
        { wch: 9 },   // FH
        ...COST_FIELDS.map(() => ({ wch: 14 })),
        { wch: 14 },  // Total
    ];

    // 行高：表头稍高
    ws['!rows'] = [{ hpt: 30 }];

    const wb = XLSX_STYLE.utils.book_new();
    XLSX_STYLE.utils.book_append_sheet(wb, ws, '成本报表');
    XLSX_STYLE.writeFile(wb, filename);

    return true;
};

// ─── 航段费用查询专用导出（列与列表页一一对应 + 列合计行）───

const LEG_COST_FIXED_COLS = [
    { key: 'operation_date', title: '日期', width: 12 },
    { key: 'flight_number', title: '航班号', width: 12 },
    { key: 'leg', title: '航段', width: 12 },
    { key: 'aircraft_registration', title: '飞机', width: 12 },
];

/**
 * 导出航段费用查询列表到 Excel（带样式 + 列合计行）
 *
 * 列顺序与列表页完全一致：固定列(日期/航班号/航段/飞机) + 由调用方传入的 costTypes。
 * costTypes 直接复用页面里的 COST_TYPES，保证列、顺序、表头与界面零漂移。
 *
 * @param {Array<Object>} items - 列表行（字段 operation_date/flight_number/leg/aircraft_registration + 各费用 key）
 * @param {Array<{key:string,title:string,isHours?:boolean}>} costTypes - 费用列定义（与页面 COST_TYPES 一致）
 * @param {Object} options - { filename }
 */
export const exportLegCostListToExcel = (items, costTypes, options = {}) => {
    const { filename = '航段费用.xlsx' } = options;

    if (!items || items.length === 0) {
        throw new Error('没有数据可导出');
    }
    if (!Array.isArray(costTypes) || costTypes.length === 0) {
        throw new Error('缺少费用列定义');
    }

    const headers = [
        ...LEG_COST_FIXED_COLS.map((c) => c.title),
        ...costTypes.map((c) => c.title),
    ];

    const rows = [];
    rows.push(headers.map((h) => buildCell(h, STYLES.header)));

    // 仅金额列参与合计（BH/FH 是小时，不做跨段求和，与页面 KPI 口径一致）
    const moneyTotals = {};
    costTypes.forEach((c) => {
        if (!c.isHours) moneyTotals[c.key] = 0;
    });

    items.forEach((it) => {
        const base = STYLES.legRow;
        const row = [
            buildCell(it.operation_date || '', base),
            buildCell(it.flight_number || '', base),
            buildCell(it.leg || '', base),
            buildCell(it.aircraft_registration || '', base),
            ...costTypes.map((c) => {
                const num = Number(it[c.key]);
                const hasValue = Number.isFinite(num) && num > 0;
                if (!c.isHours && Number.isFinite(num)) {
                    moneyTotals[c.key] += num;
                }
                const style = c.isHours ? hoursNumStyle(base) : costNumStyle(base);
                return buildCell(hasValue ? num : '', style);
            }),
        ];
        rows.push(row);
    });

    // 列合计行
    const totalStyle = STYLES.totalRow;
    const totalRow = [
        buildCell('合计', totalStyle),
        buildCell(`${items.length} 段`, totalStyle),
        buildCell('', totalStyle),
        buildCell('', totalStyle),
        ...costTypes.map((c) => {
            if (c.isHours) return buildCell('', hoursNumStyle(totalStyle));
            const sum = moneyTotals[c.key];
            return buildCell(sum > 0 ? sum : '', costNumStyle(totalStyle));
        }),
    ];
    rows.push(totalRow);

    const ws = XLSX_STYLE.utils.aoa_to_sheet(rows);

    ws['!cols'] = [
        ...LEG_COST_FIXED_COLS.map((c) => ({ wch: c.width })),
        ...costTypes.map((c) => ({ wch: Math.max(10, (c.title || '').length + 2) })),
    ];
    ws['!rows'] = [{ hpt: 28 }];

    const wb = XLSX_STYLE.utils.book_new();
    XLSX_STYLE.utils.book_append_sheet(wb, ws, '航段费用');
    XLSX_STYLE.writeFile(wb, filename);

    return true;
};
